import { useCallback, useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  useAccountPersonType,
  useBatchTransferActivityStore,
  useCurrentUser,
  useToastStore,
} from '@tankef/core-react';
import { useTranslation } from '@tankef/i18n';

import { isBatchTransferInFlight, useActiveBatchTransfers, useBatchTransfer } from '../api';
import { useBatchTransferRealtime } from '../api/useBatchTransferRealtime';

function BatchActivity({ id, onFinish }: { id: string; onFinish: (id: string) => void }) {
  const { t } = useTranslation('batchTransfers');
  const detail = useBatchTransfer(id);
  const batch = detail.data;
  const user = useCurrentUser();
  const queued = useBatchTransferActivityStore(state =>
    user?.id ? state.byUser[user.id]?.[id]?.queued : undefined
  );
  const inFlight = Boolean(queued) || !batch || isBatchTransferInFlight(batch.status);
  const realtime = useBatchTransferRealtime(id, inFlight);
  const lastStatus = useRef<string>();
  const addToast = useToastStore(state => state.addToast);
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!batch || queued || isBatchTransferInFlight(batch.status)) return;
    if (lastStatus.current === batch.status) return;
    lastStatus.current = batch.status;
    const title =
      batch.status === 'validated'
        ? 'readyNotification'
        : batch.status === 'completed'
          ? 'completedNotification'
          : 'attentionNotification';
    addToast({
      type: ['validated', 'completed'].includes(batch.status) ? 'success' : 'warning',
      title: t(title),
      message:
        batch.status === 'validated'
          ? t('paymentCount', { count: batch.total_count })
          : batch.status === 'validation_failed'
            ? t('validationFailedHint')
            : t('processedCount', { processed: batch.processed_count, total: batch.total_count }),
      duration: 8000,
    });
    void queryClient.invalidateQueries({ queryKey: ['batch-transfers'] });
    if (['completed', 'partially_completed', 'failed'].includes(batch.status)) {
      void queryClient.invalidateQueries({ queryKey: ['wallet'] });
      void queryClient.invalidateQueries({ queryKey: ['accounts', 'primary'] });
    }
    onFinish(id);
  }, [batch, queued, id, onFinish, queryClient, addToast, t]);

  if (!inFlight) return null;
  const progress = Math.min(100, Math.max(0, batch?.progress_percentage ?? 0));
  const processing = batch?.status === 'processing';
  return (
    <Link
      to={`/batch-transfers?batch=${encodeURIComponent(id)}`}
      className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-lg border border-primary-200 bg-tkf-surface px-3 py-2 text-tkf-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 sm:min-w-64"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={`h-2 w-2 shrink-0 rounded-full ${realtime.live ? 'bg-success-700' : 'bg-primary-800'}`}
            aria-hidden="true"
          />
          <span className="truncate text-xs font-semibold">
            {batch?.filename || t('activeProcess')}
          </span>
          {processing && (
            <span className="ml-auto text-xs font-bold tabular-nums">{progress}%</span>
          )}
        </div>
        <p className="mt-1 text-xs">
          {detail.isError
            ? t('activityError')
            : queued
              ? t('queuedHint')
              : processing && batch
                ? t('remainingCount', {
                    count: Math.max(0, batch.total_count - batch.processed_count),
                  })
                : batch && !['draft', 'validating'].includes(batch.status)
                  ? t('unknownStatusHint')
                  : t('validatingTitle')}
        </p>
        {processing && (
          <div
            role="progressbar"
            aria-label={t('progress')}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
            className="mt-2 h-1.5 overflow-hidden rounded-full bg-primary-100"
          >
            <div
              className="h-full bg-primary-800 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
      <ArrowUpRight size={16} className="shrink-0" aria-hidden="true" />
    </Link>
  );
}

/** Lives in DashboardLayout so progress and notifications survive page navigation. */
export function BatchTransfersActivity() {
  const { isPm } = useAccountPersonType();
  const user = useCurrentUser();
  if (!isPm || !user) return null;
  return <BatchActivityTracker key={user.id} userId={user.id} />;
}

function BatchActivityTracker({ userId }: { userId: string }) {
  const active = useActiveBatchTransfers(true, userId);
  const tracked = useBatchTransferActivityStore(state => state.byUser[userId]);
  const track = useBatchTransferActivityStore(state => state.track);
  const finish = useBatchTransferActivityStore(state => state.finish);
  const trackedIds = Object.keys(tracked ?? {});
  const { t } = useTranslation('batchTransfers');
  useEffect(() => {
    if (!active.data?.length) return;
    track(
      userId,
      active.data.map(batch => batch.id)
    );
  }, [active.data, track, userId]);
  const handleFinish = useCallback((id: string) => finish(userId, id), [finish, userId]);
  if (!trackedIds.length) return null;
  return (
    <aside
      aria-label={t('activeProcesses')}
      className="shrink-0 border-b border-primary-200 bg-primary-50 px-4 py-2"
    >
      <div className="mx-auto flex max-h-36 max-w-7xl flex-col gap-2 overflow-auto sm:flex-row sm:flex-wrap">
        {trackedIds.map(id => (
          <BatchActivity key={id} id={id} onFinish={handleFinish} />
        ))}
      </div>
    </aside>
  );
}
